import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from './index'
import type { IClientUID } from '../clients'

export enum SettingsFragment {
    CommonSettings = 0,
    SodaUnit = 1,
    AluminiumBlockCoolingUnit = 2,
    IceBankCoolingUnit = 3,
    AmbientUnit = 4,
    CirculationPumpUnitUnit = 5,
    HeatingUnit = 6,
    FilterFiller = 7,

    Last = 8,
}

export interface IFragmentedSettings {
    [fragmentVersion: number]: IFragmentedSettings_v1

    1: IFragmentedSettings_v1
    2: IFragmentedSettings_v1
    3: IFragmentedSettings_v1
    4: IFragmentedSettings_v1
}

export interface IFragmentedSettings_v1 {
    common: ICommonSettings_v1
    sodaUnit?: ISodaUnit_v1 | ISodaUnit_v2
    aluBlockCoolingUnit?: IAluBlockCoolingUnit_v1
    iceBlockCoolingUnit?: IIceBankUnit_v1 | IIceBankUnit_v2
    ambientUnit?: IAmbientUnit_v1
    circulationPumpUnit?: ICirculationPumpUnit_v1
    heatingUnit?: IHeatingUnit_v1
    filterFiller?: IFilterFiller_v1
    shabbatSettings?: IShabbatSettings_v1 | IShabbatSettings_v2 | IShabbatSettings_v3
}

interface ICommonSettings_v1 {
    wifiEnable: number
    autoSocketConnect: number
    socketTransportMode?: number
    httpPollRateMs?: number
    loggingEnabled?: number
    remoteLiveLogStreamEnabled?: number
}

interface ISodaUnit_v1 {
    sodaUnitDisabled: number
    pumpMaxTime: number
    minH2oPiezo: number
    minCo2Piezo: number
    pumpStartDelay: number
    pumpStopDelay: number
    oneLiquidLevel: number
    oneLiquidLevelDelay: number
    ignoreH2oPiezo: number
    ignoreCo2Piezo: number
}

interface ISodaUnit_v2 {
    sodaUnitDisabled: number
    pumpMaxTime: number
    minH2oPiezo: number
    minCo2Piezo: number
    pumpStartDelay: number
    pumpStopDelay: number
    oneLiquidLevel: number
    oneLiquidLevelDelay: number
    oneLiquidLevelIndicatorIsLow: number
    ignoreH2oPiezo: number
    ignoreCo2Piezo: number
}

interface IAluBlockCoolingUnit_v1 {
    coolingUnitDisabled: number
    ntc1MaxValue: number
    ntc1MinValue: number
    compressorCooldown: number
}

interface IIceBankUnit_v1 {
    coolingUnitDisabled: number
    waterValue: number
    iceValue: number
    compressorCooldown: number
}

interface IIceBankUnit_v2 extends IIceBankUnit_v1 {
    iceBankMaxWorkTimeMinutes: number
    iceBankRetryWaitMinutes: number
}

interface IAmbientUnit_v1 {
    ambientUnitEnabled: number
    ntc2MaxValue: number
    ntc2MinValue: number
    fanMaxWorkTime: number
    fanCoolTime: number
}

interface ICirculationPumpUnit_v1 {
    circulationPumpUnitEnabled: number
    circulationDelayStop: number
    circulationInterval: number
    circulationWorkTime: number
}

interface IHeatingUnit_v1 {
    heatingUnitEnabled: number
    boilerMaxHeat: number
    boilerMinHeat: number
}

interface IFilterFiller_v1 {
    state: number
    counter: number
    filterFillerActiveTime: number
    filterFillerInactiveTime: number
}

interface IShabbatSettings_v1 {
    shabbatModeEnabled: number
    shabbatHighDelayMinMs: number
    shabbatHighDelayMaxMs: number
    shabbatLowDelayMinMs: number
    shabbatLowDelayMaxMs: number
}

interface IShabbatSettings_v2 extends IShabbatSettings_v1 {
    shabbatLowCsSleepAddMs: number
}

// v3 (LTCB220/LTCB226 Shabbat cooling cycle): v2 + cooling interval/check (minutes).
interface IShabbatSettings_v3 extends IShabbatSettings_v2 {
    shabbatCoolingIntervalMinutes: number
    shabbatCoolingCheckMinutes: number
}

export enum FragmentedSettingType {
    ntc1MaxValue = 1,
    ntc1MinValue = 2,
    waterValue = 3,
    iceValue = 4,
    // fanPwmDuty = 5,
    compressorCooldown = 6,
    // pumpPwmDuty = 7,
    minH2oPiezo = 8,
    minCo2Piezo = 9,
    pumpMaxTime = 10,
    // piezo1Calib = 11,
    // piezo2Calib = 12,
    boilerMaxHeat = 13,
    boilerMinHeat = 14,
    heatingUnitEnabled = 15,
    wifiEnable = 16,
    ignoreH2oPiezo = 17,
    ignoreCo2Piezo = 18,
    oneLiquidLevel = 22,
    oneLiquidLevelDelay = 23,
    // fanDisabled = 24,
    sodaUnitDisabled = 25,
    pumpStartDelay = 26,
    pumpStopDelay = 27,
    ntc2MaxValue = 28,
    ntc2MinValue = 29,
    fanMaxWorkTime = 30,
    fanCoolTime = 31,
    circulationDelayStop = 32,
    circulationInterval = 33,
    circulationWorkTime = 34,
    circulationPumpUnitEnabled = 35,
    ambientUnitEnabled = 36,
    autoSocketConnect = 37,
    coolingUnitDisabled = 38,
    oneLiquidLevelIndicatorIsLow = 39,
    shabbatHighDelayMinMs = 40,
    shabbatHighDelayMaxMs = 41,
    shabbatLowDelayMinMs = 42,
    shabbatLowDelayMaxMs = 43,
    shabbatModeEnabled = 44,
    shabbatLowCsSleepAddMs = 45,
    iceBankMaxWorkTimeMinutes = 46,
    iceBankRetryWaitMinutes = 47,
    socketTransportMode = 48,
    httpPollRateMs = 49,
    loggingEnabled = 50,
    remoteLiveLogStreamEnabled = 51,
    // LTCB220/LTCB226 only — Shabbat cooling cycle (kShabbatSettings v3).
    shabbatCoolingIntervalMinutes = 53,
    shabbatCoolingCheckMinutes = 54,
}

export interface IFragmentedSettingUpdate {
    type: FragmentedSettingType
    value: number
}

export async function getClientFragmentedSettings(clientId: IClientUID): Promise<ApiResponse<IFragmentedSettings>> {
    const response = await fetch(`${HOST_URL}fragmented-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<IFragmentedSettings>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateClientFragmentedSettings(clientId: IClientUID, updates: IFragmentedSettingUpdate[]): Promise<ApiResponse<void>> {
    const response = await fetch(`${HOST_URL}fragmented-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, updates }),
    })
    const result = (await response.json()) as ApiResponse<void>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function restoreClientFragmentedSettings(clientId: IClientUID): Promise<ApiResponse<void>> {
    const response = await fetch(`${HOST_URL}fragmented-settings`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<void>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
